// Market-Cache-Processing. 1:1 portiert aus V1 useDataSync.js `processRawCache`.
// Transformiert die rohe Worker-Cache-Response in `FullData` für das Frontend.
// Pure functions — kein State, kein Side-Effect.

import { analyzeKlines, yahooCandlesToBinance, type YahooChartResponse } from './klines-analysis';
import type {
  AssetKlines,
  CotEntry,
  FullData,
  HLSignals,
  HLSignalsByAsset,
  JapanMacro,
  KlineRow,
  MarketStats,
  PriceSnapshot,
  YahooPrice,
} from '@/types/market';

const num = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v);
  return Number.NaN;
};

const numOrNull = (v: unknown): number | null => {
  const n = num(v);
  return Number.isFinite(n) ? n : null;
};

/** V3-Backend HL-Ctx (Subset von Hyperliquid metaAndAssetCtxs). */
interface HlCtx {
  markPx?: string | number;
  prevDayPx?: string | number;
  oraclePx?: string | number;
  funding?: string | number;
  openInterest?: string | number;
  premium?: string | number;
  dayNtlVlm?: string | number;
}

/** Rohe Worker-Cache-Response — bewusst lose typisiert, Worker garantiert Felder nicht alle.
 *
 * Backend-Schema-History:
 *   - V1 (alt): hlAssets: [universe, ctxs[]] + yahoo: { vix: ChartResponse } + fng: { data: [...] }
 *   - V3 (aktuell): hl: { mids: {SYM:"price"}, btcCtx: {markPx,...} } + yahoo: { VIX: {price,change} } + fng: {value,label}
 *
 * processRawCache erkennt automatisch welches Schema vorliegt.
 */
export interface RawMarketCache {
  // ── V3-Schema (aktueller Worker-Cron) ──
  hl?: {
    mids?: Record<string, string> | null;
    btcCtx?: HlCtx | null;
    ethCtx?: HlCtx | null;
    solCtx?: HlCtx | null;
  } | null;
  // ── V1-Schema (legacy, fallback) ──
  hlAssets?: [{ universe?: Array<{ name?: string }> }, Array<Record<string, unknown>>] | null;
  hlPredictedFundings?: unknown[] | null;
  binance?: Array<{ symbol?: string; lastPrice?: string; priceChangePercent?: string }> | null;
  // fng: kann beide Schemas sein — V3 {value,label} oder V1 {data:[...]}
  fng?:
    | { value: number; label: string }
    | { data?: Array<{ value: number | string; value_classification?: string; timestamp?: string | number }> }
    | null;
  btcFunding?: unknown;
  btcOi?: unknown;
  ethFunding?: unknown;
  ethOi?: unknown;
  solFunding?: unknown;
  polymarket?: unknown[] | null;
  // Trending: V3 = flach `[{id,symbol,name,marketCapRank}]`, V1 = `{coins:[{item:{...}}]}`
  trending?:
    | Array<{ id?: string; symbol?: string; name?: string; marketCapRank?: number | null; score?: number }>
    | { coins?: unknown[] }
    | null;
  // Global: V3 = flach `{totalMcap,btcDominance,...}`, V1 = `{data:{...}}`
  global?:
    | { totalMcap?: number; totalVolume?: number; btcDominance?: number; ethDominance?: number; mcapChange24h?: number }
    | { data?: { total_market_cap?: { usd?: number }; market_cap_percentage?: { btc?: number }; total_volume?: { usd?: number } } }
    | null;
  yahoo?: Record<string, YahooChartResponse | null | undefined> | null;
  cotRaw?: Array<Record<string, string | null>> | null;
  bojProbs?: {
    today?: {
      current_target?: number | string;
      as_of?: string;
      rows?: Array<{
        meeting_iso: string;
        meeting: string;
        prob_move_pct: number;
        prob_is_cut: boolean;
        implied_rate_post_meeting: number;
        change_bps: number;
      }>;
    };
  } | null;
  newsApi?: unknown;
  newsApiGeo?: unknown;
  fjHeadlines?: unknown[] | null;
  reuters?: unknown[] | null;
  eventScores?: unknown;
  lunarList?: unknown;
  lunarTopics?: unknown[] | null;
  lunarNews?: unknown;
  fred?: {
    jpyusd?: { observations?: Array<{ value: string; date: string }> };
    chinaTreasury?: { observations?: Array<{ value: string; date: string }> };
    jp10y?: { value: number; date: string } | null;
  } | null;
  klines?: Record<string, AssetKlines> | null;
  forexKlines?: Record<string, YahooChartResponse | null | undefined> | null;
  timestamp?: string | number | null;
}

const DEFAULT_PRICE: PriceSnapshot = { usd: 0, usd_24h_change: 0 };
const DEFAULT_HL_SIGNALS: HLSignalsByAsset = { BTC: null, ETH: null, SOL: null };

/**
 * Module-level OI-Tracking für `oiDelta`. V1 hat dieselbe Strategie (`_prevHlOI`).
 * Wird beim ersten Aufruf pro Symbol auf `null` initialisiert.
 */
const _prevHlOI: Record<'BTC' | 'ETH' | 'SOL', number | null> = {
  BTC: null,
  ETH: null,
  SOL: null,
};

/** Reset für Tests. */
export function resetOIBaseline(): void {
  _prevHlOI.BTC = null;
  _prevHlOI.ETH = null;
  _prevHlOI.SOL = null;
}

/**
 * V3-Schema-Helper: Yahoo-Quote pro Symbol auflösen.
 *
 * Backend liefert seit Phase 2 entweder:
 *   - V3: { VIX: { price: 17.95, change: 0.45 }, DXY: {...} }  ← uppercase, flach
 *   - V1: { vix: <ChartResponse>, dxy: <ChartResponse> }       ← lowercase, nested
 *
 * Liefert die V1-kompatible `YahooPrice`-Form damit alle downstream-Funktionen
 * (Stats-Derivation, FullData-Konsumenten) unverändert bleiben.
 */
/**
 * FNG-Normalisierung: V3-Backend liefert `{ value, label }` direkt, V1 lieferte
 * `{ data: [{ value, value_classification, timestamp }] }`. FullData.fng ist
 * als V1-Form typisiert — wir normalisieren V3 zu V1.
 */
/**
 * V3-Backend liefert trending als flaches `[{id, symbol, name, marketCapRank}]`,
 * V1 lieferte `{ coins: [{ item: {...} }] }`. eve-context.ts liest das V1-Schema —
 * wir normalisieren V3 hierhin, damit der Builder unverändert bleibt.
 */
function normalizeTrending(
  trending: RawMarketCache['trending'] | undefined,
): FullData['trending'] | null {
  if (!trending) return null;
  if (Array.isArray(trending)) {
    // V3-Schema: flat array
    return {
      coins: trending.map((t: unknown) => {
        const it = (t as { name?: string; symbol?: string; marketCapRank?: number | null }) ?? {};
        return {
          item: {
            name: it.name,
            symbol: it.symbol,
            market_cap_rank: it.marketCapRank ?? null,
          },
        };
      }),
    };
  }
  // V1-Schema: { coins: [...] }
  if (typeof trending === 'object' && 'coins' in trending) {
    return trending as FullData['trending'];
  }
  return null;
}

/**
 * V3-Backend liefert global als flaches `{totalMcap, btcDominance, ...}`,
 * V1 lieferte `{ data: { total_market_cap: { usd }, market_cap_percentage: { btc } } }`.
 */
function normalizeGlobal(
  global: RawMarketCache['global'] | undefined,
): FullData['global'] | null {
  if (!global) return null;
  // V1-Schema bereits
  if (typeof global === 'object' && 'data' in global) {
    return global as FullData['global'];
  }
  // V3-Schema: flat
  const g = global as {
    totalMcap?: number;
    totalVolume?: number;
    btcDominance?: number;
    ethDominance?: number;
    mcapChange24h?: number;
  };
  return {
    data: {
      total_market_cap: { usd: g.totalMcap ?? 0 },
      total_volume: { usd: g.totalVolume ?? 0 },
      market_cap_percentage: { btc: g.btcDominance ?? 0 },
    },
  };
}

function normalizeFng(
  fng: RawMarketCache['fng'] | undefined,
): { data?: Array<{ value: number | string; value_classification?: string; timestamp?: string | number }> } | null {
  if (!fng) return null;
  if (typeof fng === 'object' && 'data' in fng && Array.isArray(fng.data)) {
    return fng;
  }
  if (typeof fng === 'object' && 'value' in fng) {
    const v = fng as { value: number; label: string };
    return { data: [{ value: v.value, value_classification: v.label }] };
  }
  return null;
}

function resolveYahoo(
  raw: RawMarketCache,
  upperKey: string,
  lowerKey: string,
): YahooPrice {
  const flat = raw.yahoo?.[upperKey];
  if (flat && typeof flat === 'object' && !('chart' in flat) && 'price' in flat) {
    const p = flat as { price?: number | null; change?: number | string | null };
    return { price: p.price ?? null, change: p.change ?? null };
  }
  // V1-Fallback: lowercase key + chart-Format
  return yahooPriceFromChart(raw.yahoo?.[lowerKey] as YahooChartResponse | null | undefined);
}

/** Yahoo-Meta → price/change. V1-Format mit string-change aus `toFixed(2)`. */
export function yahooPriceFromChart(chart: YahooChartResponse | null | undefined): YahooPrice {
  const meta = chart?.chart?.result?.[0]?.meta;
  if (!meta) return { price: null, change: null };
  const prev = meta.chartPreviousClose ?? meta.previousClose;
  const curr = meta.regularMarketPrice;
  if (prev == null || curr == null) return { price: curr ?? null, change: null };
  const chg = (((curr - prev) / prev) * 100).toFixed(2);
  return { price: curr, change: chg };
}

function buildHLSignals(
  symbol: 'BTC' | 'ETH' | 'SOL',
  ctx: Record<string, unknown> | null,
): HLSignals | null {
  if (!ctx) return null;
  const mark = num(ctx['markPx']) || 0;
  const oracle = num(ctx['oraclePx']) || 0;
  const funding = num(ctx['funding']) || 0;
  const oi = num(ctx['openInterest']) || 0;
  const premium = oracle > 0 ? parseFloat((((mark - oracle) / oracle) * 100).toFixed(4)) : 0;
  const prevOI = _prevHlOI[symbol];
  const oiDelta = prevOI !== null ? parseFloat((oi - prevOI).toFixed(2)) : null;
  _prevHlOI[symbol] = oi;
  return { mark, oracle, premium, funding, oi, oiDelta };
}

function bybitFunding(r: unknown): number | string | null {
  if (Array.isArray(r) && r[0] && typeof r[0] === 'object' && 'fundingRate' in r[0]) {
    return (r[0] as { fundingRate: number | string }).fundingRate;
  }
  if (r && typeof r === 'object' && 'result' in r) {
    const list = (r as { result?: { list?: Array<{ fundingRate?: number | string }> } }).result?.list;
    return list?.[0]?.fundingRate ?? null;
  }
  return null;
}

function bybitOI(r: unknown): number | string | null {
  if (!r || typeof r !== 'object') return null;
  if ('openInterest' in r) return (r as { openInterest: number | string }).openInterest;
  const list = (r as { result?: { list?: Array<{ openInterest?: number | string }> } }).result?.list;
  return list?.[0]?.openInterest ?? null;
}

function buildCot(raw: RawMarketCache['cotRaw']): CotEntry[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.map((r) => {
    const long = parseInt(r['noncomm_positions_long_all'] ?? '0', 10);
    const short = parseInt(r['noncomm_positions_short_all'] ?? '0', 10);
    return {
      date: r['report_date_as_yyyy_mm_dd']?.slice(0, 10) ?? null,
      long,
      short,
      net: long - short,
      chgLong: parseInt(r['change_in_noncomm_long_all'] ?? '0', 10),
      chgShort: parseInt(r['change_in_noncomm_short_all'] ?? '0', 10),
      openInterest: parseInt(r['open_interest_all'] ?? '0', 10),
    };
  });
}

function fredLatest(
  series: { observations?: Array<{ value: string; date: string }> } | undefined | null,
): { value: number; date: string; change: number | null } | null {
  const obs = series?.observations?.filter((o) => o.value !== '.' && o.value !== null) ?? [];
  if (obs.length === 0) return null;
  const last = obs[obs.length - 1]!;
  const prev = obs.length > 1 ? obs[obs.length - 2]! : null;
  const val = parseFloat(last.value);
  const change =
    prev != null
      ? parseFloat((((val - parseFloat(prev.value)) / parseFloat(prev.value)) * 100).toFixed(2))
      : null;
  return { value: val, date: last.date, change };
}

function buildJapan(raw: RawMarketCache, us10y: YahooPrice, yahooJpy: YahooPrice): JapanMacro {
  // V3-Fallback: wenn FRED kein jpyusd hat, nutzen wir die Yahoo-USDJPY-Quote.
  const usdjpy = fredLatest(raw.fred?.jpyusd) ?? (yahooJpy.price != null
    ? { value: yahooJpy.price, date: new Date().toISOString().slice(0, 10), change: typeof yahooJpy.change === 'string' ? parseFloat(yahooJpy.change) : (yahooJpy.change ?? null) }
    : null);
  const chinaTreasury = fredLatest(raw.fred?.chinaTreasury);
  const jp10y = raw.fred?.jp10y ?? null;

  const us10yVal = numOrNull(us10y.price);
  const jp10yVal = jp10y?.value ?? null;
  const bondSpread =
    us10yVal !== null && jp10yVal !== null ? parseFloat((us10yVal - jp10yVal).toFixed(3)) : null;

  const bojRate = parseFloat(String(raw.bojProbs?.today?.current_target ?? 0.75));
  const policySpread = parseFloat((3.625 - bojRate).toFixed(3));

  return {
    usdjpy,
    chinaTreasury,
    jp10y,
    bondSpread,
    policySpread,
    bojMeetings:
      raw.bojProbs?.today?.rows?.slice(0, 4).map((r) => ({
        date: r.meeting_iso,
        label: r.meeting,
        hikeProb: r.prob_move_pct,
        isCut: r.prob_is_cut,
        impliedRate: r.implied_rate_post_meeting,
        changeBps: r.change_bps,
      })) ?? null,
    bojAsOf: raw.bojProbs?.today?.as_of ?? null,
    bojCurrentRate: raw.bojProbs?.today?.current_target ?? null,
  };
}

function buildForexKlines(
  raw: RawMarketCache,
  baseKlines: Record<string, AssetKlines> | null,
  goldPrice: number,
  oilPrice: number,
  silverPrice: number,
): Record<string, AssetKlines> | null {
  if (!raw.forexKlines) return baseKlines;
  const lastClose = (yahooData: YahooChartResponse | null | undefined): number => {
    const c = yahooCandlesToBinance(yahooData ?? null);
    return c && c.length > 0 ? num(c[c.length - 1]![4]) : 0;
  };
  const ak = (data: YahooChartResponse | null | undefined, price: number): ReturnType<typeof analyzeKlines> => {
    const candles = yahooCandlesToBinance(data ?? null);
    return analyzeKlines(candles as KlineRow[] | null, price);
  };
  const fk = raw.forexKlines;
  return {
    ...(baseKlines ?? {}),
    GOLD: {
      '1D': ak(fk['goldKlines1d'], goldPrice),
      '4H': ak(fk['goldKlines4h'], goldPrice),
      '1H': ak(fk['goldKlines1h'], goldPrice),
      '15M': ak(fk['goldKlines15m'], goldPrice),
      '5M': ak(fk['goldKlines5m'], goldPrice),
    },
    OIL: {
      '1D': ak(fk['oilKlines1d'], oilPrice),
      '4H': ak(fk['oilKlines4h'], oilPrice),
      '1H': ak(fk['oilKlines1h'], oilPrice),
      '15M': ak(fk['oilKlines15m'], oilPrice),
      '5M': ak(fk['oilKlines5m'], oilPrice),
    },
    SILVER: {
      '1D': ak(fk['silverKlines1d'], silverPrice),
      '4H': ak(fk['silverKlines4h'], silverPrice),
      '1H': ak(fk['silverKlines1h'], silverPrice),
      '15M': ak(fk['silverKlines15m'], silverPrice),
      '5M': ak(fk['silverKlines5m'], silverPrice),
    },
    EURUSD: {
      '1D': ak(fk['eurusdKlines1d'], lastClose(fk['eurusdKlines1d'])),
      '4H': ak(fk['eurusdKlines4h'], lastClose(fk['eurusdKlines4h'])),
      '1H': ak(fk['eurusdKlines1h'], lastClose(fk['eurusdKlines1h'])),
      '15M': ak(fk['eurusdKlines15m'], lastClose(fk['eurusdKlines15m'])),
      '5M': ak(fk['eurusdKlines5m'], lastClose(fk['eurusdKlines5m'])),
    },
    GBPJPY: {
      '1D': ak(fk['gbpjpyKlines1d'], lastClose(fk['gbpjpyKlines1d'])),
      '4H': ak(fk['gbpjpyKlines4h'], lastClose(fk['gbpjpyKlines4h'])),
      '1H': ak(fk['gbpjpyKlines1h'], lastClose(fk['gbpjpyKlines1h'])),
      '15M': ak(fk['gbpjpyKlines15m'], lastClose(fk['gbpjpyKlines15m'])),
      '5M': ak(fk['gbpjpyKlines5m'], lastClose(fk['gbpjpyKlines5m'])),
    },
  };
}

/**
 * V1 `processRawCache` — wandelt rohe Worker-Cache-Response in normalisierte FullData.
 * Erhalten:
 * - Preise via HL preferred, Binance Fallback
 * - HL-Signals (mark/oracle/premium/funding/oi/oiDelta)
 * - Yahoo macro snapshots
 * - COT-Block, FRED-Japan-Block, BOJ-Meetings
 * - Forex/Commodity Klines (analysiert)
 */
export function processRawCache(raw: RawMarketCache | null | undefined): FullData | null {
  if (!raw) return null;

  let prices: FullData['prices'] = {
    bitcoin: { ...DEFAULT_PRICE },
    ethereum: { ...DEFAULT_PRICE },
    solana: { ...DEFAULT_PRICE },
  };
  let hlSignals: HLSignalsByAsset = { ...DEFAULT_HL_SIGNALS };

  // ── V3-Schema-Branch: raw.hl.mids + raw.hl.{btcCtx,ethCtx,solCtx} ──
  // Backend-Cron (market-data.ts) schreibt seit Phase 5 alle 3 Coins.
  // mids ist { "BTC": "80941.0", "ETH": "...", "SOL": "..." }. Jeder Ctx hat
  // markPx, prevDayPx, oraclePx, funding, openInterest → für Premium + OI-Delta.
  if (raw.hl?.mids && Object.keys(raw.hl.mids).length > 0) {
    const mids = raw.hl.mids;
    const btcCtx = raw.hl.btcCtx ?? null;
    const ethCtx = raw.hl.ethCtx ?? null;
    const solCtx = raw.hl.solCtx ?? null;

    const priceFromMid = (sym: string, ctx: HlCtx | null): PriceSnapshot => {
      const mark = num(mids[sym]);
      if (ctx) {
        const prev = num(ctx.prevDayPx);
        const chg = prev > 0 ? parseFloat((((mark - prev) / prev) * 100).toFixed(2)) : 0;
        return { usd: mark, usd_24h_change: chg };
      }
      return { usd: mark, usd_24h_change: 0 };
    };

    prices = {
      bitcoin: priceFromMid('BTC', btcCtx),
      ethereum: priceFromMid('ETH', ethCtx),
      solana: priceFromMid('SOL', solCtx),
    };
    hlSignals = {
      BTC: buildHLSignals('BTC', (btcCtx as Record<string, unknown> | null) ?? null),
      ETH: buildHLSignals('ETH', (ethCtx as Record<string, unknown> | null) ?? null),
      SOL: buildHLSignals('SOL', (solCtx as Record<string, unknown> | null) ?? null),
    };
  } else if (raw.hlAssets && Array.isArray(raw.hlAssets[1])) {
    const universe = raw.hlAssets[0]?.universe ?? [];
    const ctxs = raw.hlAssets[1];
    const getCtx = (sym: string): Record<string, unknown> | null => {
      const idx = universe.findIndex((u) => u.name === sym);
      return idx >= 0 ? (ctxs[idx] ?? null) : null;
    };
    const buildPrice = (sym: string): PriceSnapshot => {
      const ctx = getCtx(sym);
      if (!ctx) return { ...DEFAULT_PRICE };
      const mark = num(ctx['markPx']) || 0;
      const prev = num(ctx['prevDayPx']) || 0;
      const chg = prev > 0 ? parseFloat((((mark - prev) / prev) * 100).toFixed(2)) : 0;
      return { usd: mark, usd_24h_change: chg };
    };
    prices = {
      bitcoin: buildPrice('BTC'),
      ethereum: buildPrice('ETH'),
      solana: buildPrice('SOL'),
    };
    hlSignals = {
      BTC: buildHLSignals('BTC', getCtx('BTC')),
      ETH: buildHLSignals('ETH', getCtx('ETH')),
      SOL: buildHLSignals('SOL', getCtx('SOL')),
    };
  } else if (Array.isArray(raw.binance)) {
    const bMap = new Map<string, { lastPrice?: string; priceChangePercent?: string }>();
    for (const t of raw.binance) {
      if (t.symbol) bMap.set(t.symbol, t);
    }
    const fromBin = (sym: string): PriceSnapshot => {
      const b = bMap.get(sym);
      return {
        usd: parseFloat(b?.lastPrice ?? '0') || 0,
        usd_24h_change: parseFloat(b?.priceChangePercent ?? '0') || 0,
      };
    };
    prices = {
      bitcoin: fromBin('BTCUSDT'),
      ethereum: fromBin('ETHUSDT'),
      solana: fromBin('SOLUSDT'),
    };
  }

  // Yahoo-Schema-Detection: V3-Schema liefert flach `{price, change}` mit
  // UPPERCASE-Keys (VIX/DXY/GOLD/SPX/SILVER/OIL/OVX/TLT/US10Y/US3M/USDJPY),
  // V1 lieferte YahooChartResponse mit lowercase keys. resolveYahoo() handlet beide.
  const dxy = resolveYahoo(raw, 'DXY', 'dxy');
  const vix = resolveYahoo(raw, 'VIX', 'vix');
  const spx = resolveYahoo(raw, 'SPX', 'spx');
  const gold = resolveYahoo(raw, 'GOLD', 'gold');
  const tlt = resolveYahoo(raw, 'TLT', 'tlt');
  const silver = resolveYahoo(raw, 'SILVER', 'silver');
  // JPY-Lookup mit Fallback: V3 schreibt `USDJPY`, V1 schrieb `jpy`.
  const jpy = (() => {
    const v3 = resolveYahoo(raw, 'USDJPY', 'usdjpy');
    if (v3.price != null) return v3;
    return resolveYahoo(raw, 'JPY', 'jpy');
  })();
  const oil = resolveYahoo(raw, 'OIL', 'oil');
  const us10y = resolveYahoo(raw, 'US10Y', 'us10y');
  const us3m = resolveYahoo(raw, 'US3M', 'us3m');
  const ovx = resolveYahoo(raw, 'OVX', 'ovx');

  const cot = buildCot(raw.cotRaw);

  const goldPrice = gold.price ?? 0;
  const oilPrice = oil.price ?? 0;
  const silverPrice = silver.price ?? 0;

  const mergedKlines = buildForexKlines(raw, raw.klines ?? null, goldPrice, oilPrice, silverPrice);

  return {
    prices,
    fng: normalizeFng(raw.fng),
    cot,
    klines: mergedKlines,
    funding: { fundingRate: hlSignals.BTC?.funding ?? bybitFunding(raw.btcFunding) },
    oi: { openInterest: hlSignals.BTC?.oi ?? bybitOI(raw.btcOi) },
    ethFunding: { fundingRate: hlSignals.ETH?.funding ?? bybitFunding(raw.ethFunding) },
    ethOi: { openInterest: hlSignals.ETH?.oi ?? bybitOI(raw.ethOi) },
    solFunding: { fundingRate: hlSignals.SOL?.funding ?? bybitFunding(raw.solFunding) },
    hlSignals,
    predictedFundings: raw.hlPredictedFundings ?? null,
    polymarket: (raw.polymarket as FullData['polymarket']) ?? null,
    trending: normalizeTrending(raw.trending),
    global: normalizeGlobal(raw.global),
    newsApi: (raw.newsApi as FullData['newsApi']) ?? null,
    newsApiGeo: (raw.newsApiGeo as FullData['newsApiGeo']) ?? null,
    fjHeadlines: (raw.fjHeadlines as FullData['fjHeadlines']) ?? null,
    reuters: (raw.reuters as FullData['reuters']) ?? null,
    eventScores: (raw.eventScores as FullData['eventScores']) ?? null,
    lunarList: (raw.lunarList as FullData['lunarList']) ?? null,
    lunarTopics: raw.lunarTopics ?? null,
    lunarNews: (raw.lunarNews as FullData['lunarNews']) ?? null,
    macro: { dxy, vix, spx, gold, tlt, silver, jpy, oil, us10y, us3m, ovx },
    japan: buildJapan(raw, us10y, jpy),
    timestamp: raw.timestamp ?? null,
  };
}

/**
 * Leitet `MarketStats` (UI-Top-Level) aus `FullData` ab. V1 hat das in App.js inline
 * gebaut — in V3 ist es ein purer Selector.
 */
export function deriveMarketStats(fullData: FullData | null): MarketStats | null {
  if (!fullData) return null;
  const fng = fullData.fng?.data?.[0];
  return {
    btc:
      fullData.prices?.bitcoin
        ? { price: fullData.prices.bitcoin.usd, change: fullData.prices.bitcoin.usd_24h_change }
        : null,
    eth:
      fullData.prices?.ethereum
        ? { price: fullData.prices.ethereum.usd, change: fullData.prices.ethereum.usd_24h_change }
        : null,
    sol:
      fullData.prices?.solana
        ? { price: fullData.prices.solana.usd, change: fullData.prices.solana.usd_24h_change }
        : null,
    dxy: fullData.macro.dxy ? { price: fullData.macro.dxy.price, change: fullData.macro.dxy.change } : null,
    vix: fullData.macro.vix ? { price: fullData.macro.vix.price, change: fullData.macro.vix.change } : null,
    spx: fullData.macro.spx ? { price: fullData.macro.spx.price, change: fullData.macro.spx.change } : null,
    gold: fullData.macro.gold ? { price: fullData.macro.gold.price, change: fullData.macro.gold.change } : null,
    fng: fng ? { value: numOrNull(fng.value), label: fng.value_classification ?? null } : null,
    carrySpread: fullData.japan.bondSpread,
    btcFunding: fullData.funding.fundingRate,
  };
}
